// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Each component does ONE thing
// 2. Separation of Concerns - Logic separated from presentation
// 3. Modular Architecture - Independent, reusable components
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

// Refer to docs/architecture/system-architecture.md

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Paper,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment
} from '@mui/material';
import { 
  Visibility, 
  VisibilityOff, 
  Psychology as AIIcon,
  Window as MicrosoftIcon 
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import {
  getSavedLoginCredentials,
  saveLoginCredentials,
  clearSavedLoginCredentials,
} from '../utils/loginFormStorage';

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);

  const { login, error, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const saved = getSavedLoginCredentials();
    if (!saved) return;
    setUsername(saved.username);
    setPassword(saved.password);
    setRememberMe(true);
  }, []);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || '/chat';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  const applyRememberMeAfterSuccess = () => {
    if (rememberMe) {
      saveLoginCredentials(username, password);
      return;
    }
    clearSavedLoginCredentials();
  };

  const handleRememberMeChange = (checked) => {
    setRememberMe(checked);
    if (!checked) {
      clearSavedLoginCredentials();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!username || !password) {
      return;
    }

    setIsLoading(true);

    try {
      const result = await login(username, password);

      if (result.success) {
        applyRememberMeAfterSuccess();
        const from = location.state?.from?.pathname || '/chat';
        navigate(from, { replace: true });
      }
    } catch (submitError) {
      console.error('Login error:', submitError);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f5f5f5',
        padding: 2
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
            backgroundColor: 'white'
          }}
        >
          {/* Header with Illustration */}
          <Box
            sx={{
              background: '#2d3748',
              color: 'white',
              p: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              position: 'relative'
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
                Welcome to Nexus
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                AI-Powered MINOR Vacations
              </Typography>
            </Box>
            <Box sx={{ 
              display: { xs: 'none', sm: 'flex' },
              alignItems: 'center',
              justifyContent: 'center',
              ml: 2
            }}>
              <AIIcon sx={{ fontSize: 120, opacity: 0.3 }} />
            </Box>
          </Box>

          {/* Login Form */}
          <CardContent sx={{ p: 4 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              {/* Username Field */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#2d3748' }}>
                  Username or Email
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Enter your username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  autoComplete="username"
                  autoFocus
                  disabled={isLoading}
                  size="medium"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white',
                      '& fieldset': {
                        borderColor: '#e2e8f0',
                      },
                      '&:hover fieldset': {
                        borderColor: '#cbd5e0',
                      },
                    }
                  }}
                />
              </Box>
              
              {/* Password Field */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#2d3748' }}>
                  Password
                </Typography>
                <TextField
                  fullWidth
                  placeholder="••••••••"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  disabled={isLoading}
                  size="medium"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          disabled={isLoading}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white',
                      '& fieldset': {
                        borderColor: '#e2e8f0',
                      },
                      '&:hover fieldset': {
                        borderColor: '#cbd5e0',
                      },
                    }
                  }}
                />
              </Box>

              {/* Remember Me */}
              <Box sx={{ mb: 3 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={rememberMe}
                      onChange={(e) => handleRememberMeChange(e.target.checked)}
                      disabled={isLoading}
                      sx={{
                        color: '#2d3748',
                        '&.Mui-checked': {
                          color: '#2d3748',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ color: '#2d3748' }}>
                      Remember me
                    </Typography>
                  }
                />
              </Box>

              {/* Log In Button */}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isLoading || !username || !password}
                sx={{
                  mb: 2,
                  py: 1.5,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 500,
                  color: 'white',
                  backgroundColor: '#2d3748',
                  '&:hover': {
                    backgroundColor: '#1a202c',
                  },
                  '&:disabled': {
                    backgroundColor: '#cbd5e0',
                    color: 'white'
                  }
                }}
              >
                {isLoading ? (
                  <>
                    <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                    Signing In...
                  </>
                ) : (
                  'Log In'
                )}
              </Button>

              {/* Sign in with Minor SSO */}
              <Button
                fullWidth
                variant="outlined"
                size="large"
                disabled={isLoading}
                startIcon={<MicrosoftIcon />}
                sx={{
                  py: 1.5,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 500,
                  color: '#2d3748',
                  borderColor: '#e2e8f0',
                  backgroundColor: 'white',
                  '&:hover': {
                    backgroundColor: '#f7fafc',
                    borderColor: '#cbd5e0',
                  },
                  '&:disabled': {
                    borderColor: '#e2e8f0',
                    color: '#a0aec0'
                  }
                }}
              >
                Sign in with Minor SSO
              </Button>
            </Box>
          </CardContent>
        </Paper>

        {/* Footer */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: '#718096' }}>
            © 2025 Nexus. Crafted with ❤️ by AVC IT
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default LoginPage;
